import React, { useState } from 'react';
import { MapPin, Navigation, SlidersHorizontal, List, Map as MapIcon, DollarSign, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface JobLocation {
  id: string;
  title: string;
  company: string;
  location: string;
  distance: string;
  salary: string;
  type: string;
  coordinates: {
    x: number;
    y: number;
  };
}
const JOB_LOCATIONS: JobLocation[] = [{
  id: '1',
  title: 'Barista',
  company: 'Coffee House',
  location: 'Downtown, Tallinn',
  distance: '0.5 km',
  salary: '$15-20/hr',
  type: 'Part-time',
  coordinates: {
    x: 45,
    y: 30
  }
}, {
  id: '2',
  title: 'Delivery Driver',
  company: 'QuickDeliver',
  location: 'Old Town, Tallinn',
  distance: '1.2 km',
  salary: '$18-25/hr',
  type: 'Full-time',
  coordinates: {
    x: 60,
    y: 45
  }
}, {
  id: '3',
  title: 'Retail Assistant',
  company: 'Fashion Store',
  location: 'Viru Center',
  distance: '2.1 km',
  salary: '$14-18/hr',
  type: 'Part-time',
  coordinates: {
    x: 30,
    y: 60
  }
}, {
  id: '4',
  title: 'Warehouse Worker',
  company: 'LogiCorp',
  location: 'Lasnamäe',
  distance: '3.5 km',
  salary: '$20-28/hr',
  type: 'Full-time',
  coordinates: {
    x: 75,
    y: 70
  }
}, {
  id: '5',
  title: 'Restaurant Server',
  company: 'Fine Dining',
  location: 'Kadriorg',
  distance: '1.8 km',
  salary: '$16-22/hr',
  type: 'Part-time',
  coordinates: {
    x: 55,
    y: 25
  }
}];
export function MapsPage() {
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [selectedJob, setSelectedJob] = useState<JobLocation | null>(null);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 transition-colors duration-200" data-id="element-92">
      {/* Header */}
      <motion.div initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.4
    }} className="bg-white dark:bg-gray-800 px-6 pt-12 pb-4 sticky top-0 z-30 shadow-sm dark:shadow-gray-900/30" data-id="element-93">
        <div className="flex justify-between items-center mb-4" data-id="element-94">
          <div data-id="element-95">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-96">
              Nearby Jobs
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-97">
              <Navigation className="w-3 h-3 inline mr-1" data-id="element-98" />
              Tallinn, Estonia
            </p>
          </div>
          <div className="flex gap-2" data-id="element-99">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-100">
              <SlidersHorizontal className="w-5 h-5" data-id="element-101" />
            </motion.button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl" data-id="element-102">
          <motion.button whileTap={{
          scale: 0.95
        }} onClick={() => setViewMode('map')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm transition-colors ${viewMode === 'map' ? 'bg-white dark:bg-gray-800 text-[#00D084] shadow-sm' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-103">
            <MapIcon className="w-4 h-4" data-id="element-104" />
            Map View
          </motion.button>
          <motion.button whileTap={{
          scale: 0.95
        }} onClick={() => setViewMode('list')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium text-sm transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-800 text-[#00D084] shadow-sm' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-105">
            <List className="w-4 h-4" data-id="element-106" />
            List View
          </motion.button>
        </div>
      </motion.div>

      <AnimatePresence mode="wait" data-id="element-107">
        {viewMode === 'map' ? <motion.div key="map" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.3
      }} className="p-6" data-id="element-108">
            {/* Map Container */}
            <div className="relative w-full h-[400px] bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl overflow-hidden shadow-lg" data-id="element-109">
              {/* Decorative map background */}
              <div className="absolute inset-0 opacity-20" data-id="element-110">
                <svg className="w-full h-full" viewBox="0 0 100 100" data-id="element-111">
                  <path d="M 10,30 Q 30,10 50,30 T 90,30" stroke="currentColor" fill="none" strokeWidth="0.5" className="text-gray-400" data-id="element-112" />
                  <path d="M 10,50 Q 30,30 50,50 T 90,50" stroke="currentColor" fill="none" strokeWidth="0.5" className="text-gray-400" data-id="element-113" />
                  <path d="M 10,70 Q 30,50 50,70 T 90,70" stroke="currentColor" fill="none" strokeWidth="0.5" className="text-gray-400" data-id="element-114" />
                  <circle cx="20" cy="20" r="15" fill="currentColor" className="text-green-200 dark:text-green-900" opacity="0.3" data-id="element-115" />
                  <circle cx="80" cy="80" r="20" fill="currentColor" className="text-blue-200 dark:text-blue-900" opacity="0.3" data-id="element-116" />
                </svg>
              </div>

              {/* Job Markers */}
              {JOB_LOCATIONS.map((job, index) => <motion.button key={job.id} initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1 + index * 0.1,
            type: 'spring',
            stiffness: 300
          }} whileHover={{
            scale: 1.2,
            zIndex: 10
          }} whileTap={{
            scale: 0.9
          }} onClick={() => setSelectedJob(job)} className="absolute" style={{
            left: `${job.coordinates.x}%`,
            top: `${job.coordinates.y}%`,
            transform: 'translate(-50%, -50%)'
          }} data-id="element-117">
                  <motion.div animate={{
              scale: selectedJob?.id === job.id ? [1, 1.2, 1] : 1
            }} transition={{
              duration: 0.5,
              repeat: selectedJob?.id === job.id ? Infinity : 0
            }} className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${selectedJob?.id === job.id ? 'bg-[#00D084] text-white' : 'bg-white dark:bg-gray-800 text-[#00D084]'}`} data-id="element-118">
                    <MapPin className="w-5 h-5" fill="currentColor" data-id="element-119" />
                  </motion.div>
                </motion.button>)}

              {/* User Location */}
              <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-120">
                <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg" data-id="element-121" />
                <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75" data-id="element-122" />
              </motion.div>
            </div>

            {/* Selected Job Card */}
            <AnimatePresence data-id="element-123">
              {selectedJob && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 20
          }} className="mt-4 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg" data-id="element-124">
                  <div className="flex justify-between items-start mb-3" data-id="element-125">
                    <div data-id="element-126">
                      <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-127">
                        {selectedJob.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-128">
                        {selectedJob.company}
                      </p>
                    </div>
                    <span className="text-xs font-medium text-[#00D084] bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-lg" data-id="element-129">
                      {selectedJob.distance} away
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-130">
                    <div className="flex items-center gap-1" data-id="element-131">
                      <MapPin className="w-4 h-4" data-id="element-132" />
                      {selectedJob.location}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-133">
                      <DollarSign className="w-4 h-4" data-id="element-134" />
                      {selectedJob.salary}
                    </div>
                  </div>
                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-[#00D084] hover:bg-[#00B074] text-white font-semibold py-2.5 rounded-xl transition-colors" data-id="element-135">
                    View Details
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </motion.div> : <motion.div key="list" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} className="p-6 space-y-3" data-id="element-136">
            {JOB_LOCATIONS.map((job, index) => <motion.div key={job.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} whileHover={{
          x: 4
        }} className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md cursor-pointer" data-id="element-137">
                <div className="flex justify-between items-start mb-2" data-id="element-138">
                  <div data-id="element-139">
                    <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-140">
                      {job.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-141">
                      {job.company}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-[#00D084] bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-lg whitespace-nowrap" data-id="element-142">
                    {job.distance}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400" data-id="element-143">
                  <div className="flex items-center gap-1" data-id="element-144">
                    <MapPin className="w-3 h-3" data-id="element-145" />
                    {job.location}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-146">
                    <DollarSign className="w-3 h-3" data-id="element-147" />
                    {job.salary}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-148">
                    <Clock className="w-3 h-3" data-id="element-149" />
                    {job.type}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}